"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { RegionMap } from "./region-map";
import { ProductGrid } from "./product-grid";
import { Button } from "@myapp/ui/button";
import { Badge } from "@myapp/ui/badge";
import { Separator } from "@myapp/ui/separator";
import { Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { clsx } from "clsx";
import { useDebounce } from "@/hooks/use-debounce";
import type { Product } from "@/types/product";
import { gangwonRegions } from "@/data/regions";
import { ProductGridSkeleton } from "./ui/skeleton-components";
import { NoProductsEmptyState, LoadingErrorState } from "./ui/empty-states";
import { FilterBar, FilterValues } from "./filter-bar";

interface ProductsResponse {
  items: Product[];
  nextCursor: string | null;
  total: number;
  hasMore: boolean;
}

interface MapWithListProps {
  className?: string;
  showFavorite?: boolean;
}

export function MapWithList({ className, showFavorite = false }: MapWithListProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Initialize filters
  const [filters, setFilters] = useState<FilterValues>({
    region: null,
    month: null,
    season: null,
  });
  
  // Initialize filters from URL after component mounts
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      setFilters({
        region: params.get('region'),
        month: params.get('month') ? parseInt(params.get('month')!) : null,
        season: params.get('season'),
      });
    }
  }, []);

  const router = useRouter();
  const searchParams = useSearchParams();
  
  // Debounce filter changes
  const debouncedFilters = useDebounce(filters, 150);

  // Sync with URL
  useEffect(() => {
    const regionParam = searchParams.get("region");
    const monthParam = searchParams.get("month");
    const seasonParam = searchParams.get("season");
    
    const urlFilters: FilterValues = {
      region: regionParam,
      month: monthParam ? parseInt(monthParam) : null,
      season: seasonParam,
    };
    
    // Update filters if URL changed
    if (
      urlFilters.region !== filters.region ||
      urlFilters.month !== filters.month ||
      urlFilters.season !== filters.season
    ) {
      setFilters(urlFilters);
    }
  }, [searchParams]);

  const fetchProducts = useCallback(async (filterValues: FilterValues, reset = false, currentCursor: string | null = null) => {
    if (reset) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams();
      if (filterValues.region) params.set("region", filterValues.region);
      if (filterValues.month) params.set("month", filterValues.month.toString());
      if (filterValues.season) params.set("season", filterValues.season);
      if (currentCursor && !reset) params.set("cursor", currentCursor);
      params.set("take", "12"); // Load 12 items per page

      // Simulate slow network for testing (remove in production)
      if (process.env.NODE_ENV === 'development') {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      const response = await fetch(`/api/products?${params.toString()}`);
      
      if (!response.ok) {
        const errorMessage = response.status === 404 
          ? '요청한 정보를 찾을 수 없습니다'
          : response.status >= 500 
          ? '서버 오류가 발생했습니다'
          : '특산물 정보를 불러올 수 없습니다';
        throw new Error(errorMessage);
      }

      const data: ProductsResponse = await response.json();

      if (reset) {
        setProducts(data.items);
      } else {
        setProducts(prev => [...prev, ...data.items]);
      }

      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
      setTotal(data.total);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch products");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load products when filters change
  useEffect(() => {
    setCursor(null);
    fetchProducts(debouncedFilters, true);
  }, [debouncedFilters, fetchProducts]);

  const handleRegionSelect = useCallback((regionId: string) => {
    const newFilters = { ...filters, region: regionId };
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    if (newFilters.region) params.set("region", newFilters.region);
    if (newFilters.month) params.set("month", newFilters.month.toString());
    if (newFilters.season) params.set("season", newFilters.season);
    router.replace(`?${params.toString()}`);
  }, [router, filters]);
  
  const handleFilterChange = useCallback((newFilters: FilterValues) => {
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    if (newFilters.region) params.set("region", newFilters.region);
    if (newFilters.month) params.set("month", newFilters.month.toString());
    if (newFilters.season) params.set("season", newFilters.season);
    router.replace(`?${params.toString()}`);
  }, [router]);

  const handleLoadMore = useCallback(() => {
    if (cursor && !loadingMore && !loading) {
      fetchProducts(debouncedFilters, false, cursor);
    }
  }, [cursor, loadingMore, loading, debouncedFilters, fetchProducts]);

  const handleRetry = useCallback(() => {
    fetchProducts(debouncedFilters, true);
  }, [debouncedFilters, fetchProducts]);

  const handleFavoriteToggle = useCallback((productId: string) => {
    console.log("Favorite toggled for product:", productId);
    // TODO: Implement favorite functionality
  }, []);

  const selectedRegionData = filters.region 
    ? gangwonRegions.find(r => r.id === filters.region)
    : null;

  return (
    <div className={clsx("space-y-6", className)}>
      {/* 지역 지도 */}
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">
            강원도 지역별 특산물 찾기
          </h2>
          <p className="text-muted-foreground">
            지도에서 지역을 선택하여 해당 지역의 특산물을 확인해보세요
          </p>
        </div>
        
        <RegionMap 
          onSelect={handleRegionSelect}
          className="max-w-4xl mx-auto"
        />
      </div>

      <Separator />
      
      {/* 필터 바 */}
      <FilterBar
        value={filters}
        onChange={handleFilterChange}
        className="max-w-4xl mx-auto"
      />

      <Separator />

      {/* 특산물 리스트 섹션 */}
      <div className="space-y-4">
        {(selectedRegionData || filters.month || filters.season) ? (
          <>
            {/* 헤더 정보 */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="space-y-1">
                <h3 className="text-xl font-semibold text-foreground">
                  {selectedRegionData?.title || '제철'} 특산물
                </h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {loading ? "로딩 중..." : `총 ${total}개`}
                  </Badge>
                  <Badge variant="secondary">
                    조회수 순
                  </Badge>
                </div>
              </div>
              
              {error && (
                <Button 
                  onClick={handleRetry}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  다시 시도
                </Button>
              )}
            </div>

            {/* 에러 상태 */}
            {error && !loading && (
              <LoadingErrorState
                error={error}
                onRetry={handleRetry}
              />
            )}

            {/* 로딩 상태 - 초기 로딩 */}
            {loading && products.length === 0 && (
              <ProductGridSkeleton count={8} />
            )}

            {/* 빈 상태 */}
            {!loading && !error && products.length === 0 && (
              <NoProductsEmptyState
                onReset={() => {
                  setFilters({ region: null, month: null, season: null });
                  router.replace('/');
                }}
              />
            )}

            {/* 상품 그리드 */}
            {!error && products.length > 0 && (
              <>
                <ProductGrid
                  items={products}
                  loading={loading && products.length > 0}
                  showFavorite={showFavorite}
                  onFavoriteToggle={handleFavoriteToggle}
                />

                {/* 더 보기 버튼 */}
                {hasMore && !loading && !error && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={handleLoadMore}
                      variant="outline"
                      disabled={loadingMore}
                      className="gap-2"
                    >
                      {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loadingMore ? "로딩 중..." : "더 보기"}
                    </Button>
                  </div>
                )}

                {/* 끝 표시 */}
                {!hasMore && products.length > 0 && !loading && (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">
                      모든 특산물을 불러왔습니다
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mb-4">
              <div className="w-8 h-8 bg-muted-foreground/20 rounded" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              필터를 선택해주세요
            </h3>
            <p className="text-muted-foreground max-w-md">
              지도에서 지역을 선택하거나, 월/계절 필터를 사용하여 제철 특산물을 찾아보세요.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}